import { useGetCarrierConnectCompanyAndUserTenantRelationshipsQuery } from '@connect-carrier/generated/queries/getCarrierConnectCompanyAndUserTenantRelationships';
import { connectCarrierConfig } from '@connect-carrier/harness/config';
import {
  getSelectedTenantCode,
  setSelectedCustomer,
} from '@connect-carrier/harness/customer';
import { useFlagMe405114EnableCarrierPortalMultipleCarrierImplementation } from '@generated/flags/ME-405114-enable-carrier-portal-multiple-carrier-implementation';
import { useFlagMe442723EnableFlagSegmentationForCarrierConnectFe } from '@generated/flags/ME-442723-enable-flag-segmentation-for-carrier-connect-fe';
import { useFlagMe480232CarrierPortalNavigationBrandingSearchFe } from '@generated/flags/ME-480232-carrier-portal-navigation-branding-search-fe';
import { getLDContext } from '@utils/initLaunchDarkly';
import { reportCustomError } from '@utils/reportCustomError';
import { LDMultiKindContext, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef } from 'react';

// ts-unused-exports:disable-next-line
export interface CustomerListOutput {
  companyId: string;
  createdDate: string;
  displayName?: string;
  isActive?: boolean;
  tenantImage?: string;
  tenantCode?: string;
  tenantId: string;
  mmRecordId?: string;
  mmApprovedRecordIds?: Maybe<readonly string[]>;
  tenantName?: string;
  type?: string;
  userId: string;
}

interface UseFetchCustomerListResult {
  readonly customerList: ReadonlyArray<CustomerListOutput>;
  readonly loading: boolean;
}

// ts-unused-exports:disable-next-line
export const useFetchCustomerList = (): UseFetchCustomerListResult => {
  const enableMultipleCarriers =
    useFlagMe405114EnableCarrierPortalMultipleCarrierImplementation();
  const enableFlagSegmentationForCarrierConnect =
    useFlagMe442723EnableFlagSegmentationForCarrierConnectFe();
  const enableNavigationBrandingSearchFe =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  const ldClient = useLDClient();
  const hasIdentifiedRef = useRef(false);

  const queryResult =
    useGetCarrierConnectCompanyAndUserTenantRelationshipsQuery({
      variables: {
        startIndex: 0,
        pageSize: 100,
        endIndex: 100,
      },
    });

  const userTenantRelationships =
    queryResult.data?.getCarrierConnectCompanyAndUserTenantRelationships
      .userTenantRelationships?.relationships;
  const companyRelationships =
    queryResult.data?.getCarrierConnectCompanyAndUserTenantRelationships
      ?.companyRelationships;

  const result: CustomerListOutput[] = useMemo(() => {
    const data: CustomerListOutput[] = [];
    companyRelationships?.forEach((companyRelationship) => {
      const find = userTenantRelationships?.find((userTenantRelationship) => {
        return userTenantRelationship.tenantId === companyRelationship.tenantId;
      }) as CustomerListOutput;
      if (find) {
        data.push({
          ...find,
          companyId: `${companyRelationship.companyId}`,
          mmRecordId: `${companyRelationship.mmRecordId}`,
          ...(enableMultipleCarriers && {
            mmApprovedRecordIds: companyRelationship?.mmApprovedRecordIds,
          }),
        });
      }
    });
    return data;
  }, [companyRelationships, userTenantRelationships, enableMultipleCarriers]);

  useEffect(() => {
    if (result.length > 0) {
      // Check localStorage for previously selected tenant code (behind feature flag)
      if (enableNavigationBrandingSearchFe) {
        const storedTenantCode = getSelectedTenantCode();
        if (storedTenantCode) {
          // Find customer matching the stored tenant code
          const matchingCustomer = result.find(
            (c) => c.tenantCode === storedTenantCode,
          );
          if (matchingCustomer) {
            setSelectedCustomer(matchingCustomer);
            return;
          }
        }
      }
      // Fall back to first customer if no stored tenant code or no match
      setSelectedCustomer(result[0]);
    }
  }, [result, enableNavigationBrandingSearchFe]);

  // Re-identify LaunchDarkly with tenant context after data is fetched
  // This effect runs once after initial login when customer data becomes available.
  useEffect(() => {
    const refreshLDFlags = async (): Promise<void> => {
      // Guard: Feature flag check - only run if flag is enabled
      if (!enableFlagSegmentationForCarrierConnect) {
        return;
      }

      // Guard: Ensure we have all required data
      if (
        !ldClient ||
        result.length === 0 ||
        !queryResult.data ||
        queryResult.loading
      ) {
        return;
      }

      // Use stored tenant code to find the correct customer (behind feature flag)
      const selectedCustomer = enableNavigationBrandingSearchFe
        ? ((): CustomerListOutput | undefined => {
            const storedTenantCode = getSelectedTenantCode();
            return storedTenantCode
              ? (result.find((c) => c.tenantCode === storedTenantCode) ??
                  result[0])
              : result[0];
          })()
        : result[0];

      if (!selectedCustomer) {
        return;
      }

      if (!selectedCustomer?.tenantCode || !selectedCustomer?.tenantName) {
        return;
      }

      try {
        // Get current context to check if tenant is already set
        const currentContext = ldClient.getContext();
        const multiContext = currentContext as unknown as LDMultiKindContext;

        // Check if tenant context already exists with the same key
        // If it does, no need to re-identify
        if (
          multiContext.tenant &&
          typeof multiContext.tenant === 'object' &&
          'key' in multiContext.tenant &&
          multiContext.tenant.key === selectedCustomer.tenantCode
        ) {
          // Only set the ref if tenant is actually already configured
          hasIdentifiedRef.current = true;
          return;
        }

        // Guard: Only run once per session - check AFTER confirming tenant needs to be set
        if (hasIdentifiedRef.current) {
          return;
        }

        // Build new multi-kind context with tenant information
        // Use getLDContext to ensure consistent context structure
        const newContext = getLDContext({
          launchDarklyKey: connectCarrierConfig.launchDarklyKey,
          appName: 'connect-carrier-frontend',
          env: connectCarrierConfig.envKey,
          additionalContext: {
            tenant: {
              kind: 'tenant' as const,
              name: selectedCustomer.tenantName,
              key: selectedCustomer.tenantCode,
            },
          },
        });

        // Re-identify with tenant context - this will re-evaluate all flags
        // Wait for the identify to complete before continuing
        await ldClient.identify(newContext);

        // Only set the flag AFTER successful identification
        hasIdentifiedRef.current = true;
      } catch (error) {
        reportCustomError(
          `[useFetchCustomerList] Failed to re-identify LaunchDarkly context: ${error}`,
        );
      }
    };

    refreshLDFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ldClient,
    result,
    queryResult.data,
    queryResult.loading,
    enableFlagSegmentationForCarrierConnect,
    enableNavigationBrandingSearchFe,
  ]);

  return { customerList: result || [], loading: queryResult.loading };
};
